import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';

export interface SegmentoCompite<T extends string> {
  valor: T;
  texto: string;
  /** El número al lado («Por decidir 29»). null o ausente = no se muestra. */
  cuenta?: number | null;
}

/**
 * Un control segmentado en píldora (la maqueta, arriba de las tablas): «Por
 * decidir · Aprobadas · Descartadas · Aplicadas solas». Es el mismo grupo de
 * botones excluyentes que `GrupoDeOpciones` — `role="group"` y `aria-pressed`,
 * con el nombre accesible «Por decidir 29» — con la piel de Compite.
 */
export function SegmentosCompite<T extends string>({
  opciones,
  valor,
  onCambiar,
  className,
  'aria-label': etiqueta,
}: {
  opciones: Array<SegmentoCompite<T>>;
  valor: T;
  onCambiar: (v: T) => void;
  className?: string;
  'aria-label': string;
}) {
  return (
    <div
      role="group"
      aria-label={etiqueta}
      className={cn(
        'inline-flex max-w-full flex-wrap items-center gap-1 rounded-full border bg-muted/60 p-1',
        className
      )}
    >
      {opciones.map((o) => {
        const elegida = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            aria-pressed={elegida}
            onClick={() => onCambiar(o.valor)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition-colors max-md:min-h-[40px]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              elegida
                ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {o.texto}
            {/* El espacio es para el nombre accesible («Por decidir 29»); la separación la da `gap`. */}
            {o.cuenta != null && ' '}
            {o.cuenta != null && (
              <span
                className={cn(
                  'min-w-[1.4rem] rounded-full px-1.5 py-px text-center text-xs font-bold tabular-nums',
                  elegida ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground/70'
                )}
              >
                {formatoCantidad(o.cuenta)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
